import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useFullScreenLoader } from "../../../contexts/fullScreenLoader";
import { useToasts } from "../../../contexts/toasts";
import { ClusterNodeNote } from "../../../models/Cluster";
import { Clusters } from "../../../api/Clusters";
import { Option, PagedResults } from "../../../models/generics";
import { debounce } from "lodash";
import { Pagination, Select } from "antd";
import { Alert, Breadcrumb, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import NoteItem from "../../../components/list-items/NoteItem";
import { Helmet } from "react-helmet";
import AppSearch from "../../../components/common/AppSearch";
import NoteForm from "../../../components/forms/NoteForm";
import AppLoader from "../../../components/common/AppLoader";

export default function NotesList() {

  const id = window.__env.DEFAULT_CLUSTER_ID || 0;
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  //const [cluster, setCluster] = useState<Cluster>();
  const [notes, setNotes] = useState<ClusterNodeNote[]>();
  const [nodesOptions, setNodesOptions] = useState<Option[]>();
  const [error, setError] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ClusterNodeNote>();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [totalNotes, setTotalNotes] = useState<number>();
  const [selectedNode, setSelectedNode] = useState<string>();

  const initCluster = useCallback(() => {
    Clusters.getCluster(+id!)
      .then(
        ({ data }) => {
          //setCluster(data);
          setNodesOptions(data.json.items?.map((node: any) => ({
            label: node.metadata.name,
            value: node.metadata.uid,
          })) || []);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('cluster-error', message, `Cluster Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      )
  }, [id, showToast]);

  const initClusterNotes = useCallback(() => {
    const params = new URLSearchParams();
    params.append("clusterId", `${id!}`);
    params.set("page", `${currentPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (searchText.length) params.set("freeText", searchText);
    if (selectedNode) params.append("nodeId", selectedNode);
    Clusters.getNotes(params).then(
      ({ data }) => {
        setNotes((data as PagedResults<ClusterNodeNote>).content);
        setTotalNotes((data as PagedResults<ClusterNodeNote>).pageMetadata.totalElements);
      },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('notes-error', message, `Notes Error ${status ? `(${status})` : ''}`, 'danger');
        setError(true);
      }
    )
  }, [id, currentPage, searchText, showToast, selectedNode]);

  const handleNoteModalClose = useCallback(() => {
    setSelectedNote(undefined);
    setShowNoteModal(false);
  }, []);

  const handleEditNote = useCallback((note?: ClusterNodeNote) => () => {
    setSelectedNote(note);
    setShowNoteModal(true);
  }, []);

  const handleDeleteNoteModal = useCallback((note: ClusterNodeNote) => () => {
    setSelectedNote(note);
    setShowDeleteNoteModal(true);
  }, []);

  const handleDeleteNoteModalClose = useCallback(() => {
    setSelectedNote(undefined);
    setShowDeleteNoteModal(false);
  }, []);

  const reset = useCallback(() => {
    setNotes(undefined);
    setCurrentPage(1);
    setTotalNotes(undefined);
    initCluster();
    initClusterNotes();
  }, [initCluster, initClusterNotes]);

  useEffect(() => {
    if (id) reset();
  }, [id, reset, currentPage, searchText, selectedNode]);

  const handleSearchChange = useCallback((search: string) => setSearchText(search), []);

  const debounceNotesSearch = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  useEffect(() => {
    return () => {
      debounceNotesSearch.cancel();
    };
  }, [debounceNotesSearch]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!notes) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return notes!.length === 0 ? (
      <Alert className="py-2 m-2">
        <FontAwesomeIcon icon={solid('info-circle')} />
        <span className="ms-2">No Notes found.</span>
      </Alert>
    ) : (
      <Row className="p-2 mx-0 gx-3">
        {notes!.map(item => (
          <Col key={item.id!} md={3} sm={6}>
            <NoteItem note={item} onEdit={handleEditNote(item)} onDelete={handleDeleteNoteModal(item)} />
          </Col>
        ))}
      </Row>
    );
  }, [notes, handleDeleteNoteModal, handleEditNote, error]);

  const deleteNote = useCallback(() => {
    showLoader();

    Clusters.deleteNote(selectedNote!.id!)
      .then(
        () => {
          hideLoader();
          handleDeleteNoteModalClose();
          reset();
          showToast('note-success', `Note deleted successfully`, `Cluster Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('note-error', message, `Note Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleDeleteNoteModalClose, selectedNote, reset, showToast]);

  const handleNoteSubmit = useCallback((values: any) => {
    showLoader();
    const body = {
      ...values,
      clusterId: +id!,
    }
    const promise = selectedNote ? Clusters.editNote(selectedNote.id!, body) : Clusters.createNote(body);

    promise.then(
      () => {
        hideLoader();
        showToast('note-success', `Note ${selectedNote ? 'updated' : 'created'} successfully`, 'Note Success', 'success');
        handleNoteModalClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('note-error', message, `Note Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [showLoader, showToast, hideLoader, reset, handleNoteModalClose, id, selectedNote]);

  return (
    <Container className="mt-3">
      <Helmet>
        <title>Cluster Notes</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel' }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel/cluster-management' }}>Cluster Management</Breadcrumb.Item>
        {/* <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/control-panel/clusters-management/${id}` }}>{cluster?.name}</Breadcrumb.Item> */}
        <Breadcrumb.Item active>Notes</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Notes List</h4>
        </Col>

        <Col md={3}>
          <AppSearch placeholder="Search Note..." onChange={debounceNotesSearch} />
        </Col>

        <Col md={2}>
          <Select
            allowClear
            loading={!nodesOptions}
            placeholder="Select Node"
            onChange={setSelectedNode}
            options={nodesOptions}
            value={selectedNode}
            className="w-100"
          />
        </Col>

        <Col className="text-end">
          <Button size="sm" variant="success" onClick={handleEditNote()}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create Note</span>
          </Button>
        </Col>
      </Row>
      <Row className="my-3">
        <Pagination
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          total={totalNotes}
          pageSize={20}
          current={currentPage}
          onChange={setCurrentPage}
          className="d-flex justify-content-center align-items-center"
        />
      </Row>

      {renderAsyncContent()}

      <Modal show={showNoteModal} onHide={handleNoteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedNote ? "Update" : "Create New"} Note</Modal.Title>
        </Modal.Header>
        <NoteForm note={selectedNote} nodesOptions={nodesOptions || []} onCancel={handleNoteModalClose} onSubmit={handleNoteSubmit} />
      </Modal>

      <Modal show={showDeleteNoteModal} onHide={handleDeleteNoteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete Note <b>{selectedNote?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteNoteModalClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteNote}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}